// components/clubs/ClubDataExportView.tsx
// -----------------------------------------------------------------------------
// Request and download a club data export. Pushed onto the SidePanelStack from
// ManageClubPanel (scope "club") and ClubInfoPanel (scope "self").
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { SidePanelBody } from "@/components/ui/SidePanel";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Switch } from "@/components/ui/Switch";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import type { Club, ClubExportJob, ClubExportScope } from "@/api/clubsApi";
import {
  useFetchClubExportJobsQuery,
  useRequestClubExportMutation,
} from "@/api/clubsApi";
import {
  describeJobProgress,
  formatExportDate,
  formatJobStatus,
  hasJobInFlight,
  isExpired,
  isJobInFlight,
  isJobStale,
} from "./clubDataExportUtils";

interface ClubDataExportViewProps {
  club: Club;
  scope: ClubExportScope;
}

const clubScopeIncludes = [
  "Club profile, guidelines and settings",
  "Members with their roles, join dates and contact details",
  "Events, RSVPs and a calendar (.ics) file",
  "Posts, comments and poll results",
  "Links and uploaded files",
];

const selfScopeIncludes = [
  "Your profile and membership in this club",
  "Your posts, comments and likes",
  "Your RSVPs and poll votes",
  "The photos, videos and files you uploaded",
];

/**
 * ClubDataExportView - explains what an export contains, requests one, and lists
 * recent export jobs with their download links.
 */
export function ClubDataExportView({ club, scope }: ClubDataExportViewProps) {
  const { showToast } = useToast();
  const [includeMedia, setIncludeMedia] = useState(true);
  const [isPolling, setIsPolling] = useState(false);

  const clubId = club.id;

  const {
    data: jobs,
    isLoading,
    isError,
    refetch,
  } = useFetchClubExportJobsQuery(
    { clubId: clubId!, scope },
    { skip: !clubId, pollingInterval: isPolling ? 5000 : 0 }
  );

  // Poll only while something is still being built.
  useEffect(() => {
    setIsPolling(hasJobInFlight(jobs));
  }, [jobs]);

  const [requestClubExport, { isLoading: isRequesting }] =
    useRequestClubExportMutation();

  const now = new Date();
  const activeJob = (jobs ?? []).find(
    (job) => isJobInFlight(job) && !isJobStale(job, now)
  );

  const includes = scope === "club" ? clubScopeIncludes : selfScopeIncludes;

  const handleRequestExport = async () => {
    if (!clubId) return;
    try {
      await requestClubExport({ clubId, scope, includeMedia }).unwrap();
      showToast(
        "We're preparing your export. We'll email you when it's ready.",
        "success"
      );
    } catch (error) {
      // The RTK-unwrapped rejection is a plain object: the CUSTOM_ERROR payload
      // carries the edge function message in `.error`, real Errors in `.message`.
      const err = error as { error?: unknown; message?: unknown };
      const message =
        typeof err?.error === "string"
          ? err.error
          : typeof err?.message === "string"
            ? err.message
            : undefined;
      showToast(message ?? "Failed to request export. Please try again.", "error");
    }
  };

  return (
    <SidePanelBody className="pt-0">
      <div className="space-y-6 pb-4">
        {/* What's included */}
        <section className="bg-surface rounded-2xl p-4">
          <h3 className="text-body-2-medium text-text-secondary mb-2">
            {scope === "club"
              ? "A full copy of this club's data"
              : "A copy of your data in this club"}
          </h3>
          <p className="text-body-2 text-text-secondary mb-3">
            We build a zip file with everything in open formats (CSV, Markdown
            and JSON) so you can read it anywhere.
          </p>
          <ul className="space-y-2">
            {includes.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <Icon
                  name="circle-check"
                  size={14}
                  className="text-text-secondary mt-1"
                />
                <span className="text-body-2 text-text-primary">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Options + request */}
        <section className="bg-surface rounded-2xl p-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-body-2 text-text-primary">
                Include photos and videos
              </p>
              <p className="text-body-3 text-text-tertiary">
                Larger file, takes longer to prepare.
              </p>
            </div>
            <Switch
              checked={includeMedia}
              onCheckedChange={setIncludeMedia}
              disabled={isRequesting}
            />
          </div>

          <Button
            variant="inverted"
            size="pill-lg"
            className="w-full"
            onClick={handleRequestExport}
            disabled={!clubId || isRequesting || !!activeJob}
          >
            {isRequesting ? "Requesting..." : "Request export"}
          </Button>

          <p className="text-body-3 text-text-tertiary">
            Exports run in the background. We'll email you a download link when
            yours is ready.
          </p>
        </section>

        {/* Recent exports */}
        <section>
          <h3 className="text-body-2-medium text-text-secondary mb-2">
            Recent exports
          </h3>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full rounded-2xl" />
              <Skeleton className="h-16 w-full rounded-2xl" />
            </div>
          ) : isError ? (
            // A failed query must not read as "you have never done this" — the
            // obvious next step would be requesting a duplicate export.
            <div className="space-y-1">
              <p className="text-body-3 text-accent-negative">
                We couldn't load your recent exports.
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-body-3 text-text-secondary hover:underline"
              >
                Try again
              </button>
            </div>
          ) : (jobs ?? []).length === 0 ? (
            <p className="text-body-3 text-text-tertiary">
              You haven't requested an export yet.
            </p>
          ) : (
            <div className="bg-surface rounded-2xl overflow-hidden">
              {(jobs ?? []).map((job, index) => (
                <div key={job.id}>
                  {index > 0 && <div className="h-px bg-border-subtle" />}
                  <ExportJobRow job={job} now={now} />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </SidePanelBody>
  );
}

// A single row in the recent exports list
function ExportJobRow({ job, now }: { job: ClubExportJob; now: Date }) {
  const expired = isExpired(job.expires_at, now);
  const stale = isJobStale(job, now);

  // `now` is recomputed on every render, and the 5s poll re-renders us, so the
  // estimate below keeps counting down without a ticker of its own.
  const progress = isJobInFlight(job) ? describeJobProgress(job, now) : null;
  const progressLine = progress
    ? [progress.detail, progress.eta].filter(Boolean).join(" · ")
    : "";

  return (
    <div className="p-4 space-y-1">
      <div className="flex items-center gap-3">
        <span className="flex-1 text-body-2 text-text-primary">
          {formatExportDate(job.created_at)}
        </span>
        <span className="text-body-3 text-text-secondary">
          {progress ? progress.label : formatJobStatus(job, now)}
        </span>
      </div>

      {progressLine && (
        <p className="text-body-3 text-text-tertiary">{progressLine}</p>
      )}

      {job.status === "ready" && !expired && job.download_url && (
        <div className="flex flex-col gap-1">
          <a
            href={job.download_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-body-2-medium text-text-primary hover:underline"
          >
            <Icon name="arrow-down-to-bracket" size={14} />
            Download
          </a>
          {job.expires_at && (
            <span className="text-body-3 text-text-tertiary">
              Link expires {formatExportDate(job.expires_at)}
            </span>
          )}
        </div>
      )}

      {job.status === "failed" && (
        <p className="text-body-3 text-accent-negative">
          {job.error_message || "Something went wrong preparing this export."}
        </p>
      )}

      {isJobInFlight(job) && stale && (
        <p className="text-body-3 text-text-tertiary">
          Taking longer than expected. You can request a new export.
        </p>
      )}
    </div>
  );
}
